import { ParentProps, createContext, onCleanup, onMount } from 'solid-js'
import { registerServerMessage, requireContext } from 'common'
import { visitorManagementListener as api } from 'st-app/api'
import { VisitorManagementListenerContext, notifyQueueChanged, refreshAndOffer, resetQueueState, stopQueue } from './VisitorManagementListenerContext'
import styles from './st-visitor-management-listener.module.css'

const VisitorManagementListenerContextJsx = createContext<VisitorManagementListenerContext>();
export const requireVisitorManagementListenerContext = () => {
    return requireContext(VisitorManagementListenerContextJsx);
}

export class VisitorManagementListenerProps { }

export default function VisitorManagementListener(props: ParentProps<VisitorManagementListenerProps>) {
    const context = new VisitorManagementListenerContext();

    return (
        <VisitorManagementListenerContextJsx.Provider value={context}>
            <VisitorManagementListenerInternal {...props} />
        </VisitorManagementListenerContextJsx.Provider>
    )
}

let userGU = '';

/** The visitor grid's view GUID - what a refresh broadcast makes us re-read. */
const GRID_VIEW_GUID = '8A5CC858-0284-4E7A-B6F8-5F539DD9F07B';

/**
 * Org year GUs whose changes this grid actually shows, from api.GetRefreshScope() at mount.
 *
 * EMPTY MEANS "PROCESS EVERYTHING", never "process nothing". The server returns an empty list on any
 * failure and a district-focused writer legitimately stamps an empty org year, so both sides of this
 * filter fail open by construction. A scoping fault must cost a redundant refresh; a grid that
 * silently stops updating is the one outcome that would not be noticed until someone walked past an
 * unattended visitor.
 */
let refreshScope = new Set<string>();

/**
 * Shortest gap between two refreshes. One visitor's lifecycle is several writes - insert on scan,
 * status on screening, status again on resolution, checkout stamp - and the BO broadcasts on every
 * AfterInsert AND AfterUpdate by design (the guard it replaced was broken). Each of those arriving
 * separately cost a full grid re-read plus a review-queue fetch, so a single person checking in could
 * spend four of each, per subscribed client.
 */
const REFRESH_WINDOW_MS = 500;

let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let lastRefreshAt = 0;
let queueChangedPending = false;

/**
 * Does the actual work a broadcast asks for, at most once per REFRESH_WINDOW_MS.
 *
 * THROTTLE WITH A TRAILING EDGE, NOT A PLAIN DEBOUNCE. The first message fires immediately, so a
 * quiet screen still updates the instant somebody scans in - staff are watching this grid and a
 * deliberate half-second lag on the common case would be a regression. Only a SECOND message inside
 * the window is deferred, and however many arrive during it collapse into one trailing refresh. A
 * plain trailing debounce would have been simpler and wrong: under a sustained arrival rate faster
 * than the window it never fires at all, which is exactly the busy morning this is meant to survive.
 */
function scheduleRefresh() {
    // Latched across a coalesced burst: ANY message in the window means a real write happened, so the
    // dismissed set must be cleared even though only one refresh will run. Losing this would leave a
    // visitor the operator had closed ineligible to reopen after somebody re-flagged them.
    queueChangedPending = true;

    if (refreshTimer) {
        return;
    }

    const sinceLast = Date.now() - lastRefreshAt;
    const wait = sinceLast >= REFRESH_WINDOW_MS ? 0 : REFRESH_WINDOW_MS - sinceLast;

    refreshTimer = setTimeout(() => {
        refreshTimer = null;
        lastRefreshAt = Date.now();

        window.ST.RefreshViews(GRID_VIEW_GUID)

        // A broadcast means the queue really changed - someone was flagged or someone was resolved - so
        // visitors merely CLOSED up to now become eligible again and the oldest unresolved one is offered
        // next. This is the only signal that distinguishes a real write from a plain close, which produces
        // no broadcast at all. Must precede the refresh below or that call would still be filtering
        // against the stale set.
        if (queueChangedPending) {
            queueChangedPending = false;
            notifyQueueChanged()
        }

        // Deliberately after RefreshViews and not awaiting it: the grid re-read and the queue fetch are
        // independent, and the auto-open must not be blocked by however long the grid takes to redraw.
        // refreshAndOffer opens nothing if a wizard is already outstanding, so a broadcast arriving while
        // staff are mid-wizard cannot render over their work.
        void refreshAndOffer()
    }, wait);
}

/** Drops any coalesced refresh still waiting, so no timer outlives the screen. */
function cancelScheduledRefresh() {
    if (refreshTimer) {
        clearTimeout(refreshTimer);
        refreshTimer = null;
    }
    queueChangedPending = false;
}

function VisitorManagementListenerInternal(props: ParentProps<VisitorManagementListenerProps>) {
    onMount(async () => {
        userGU = await api.StartListening();
        console.log('listening for visitor management events...');

        // Module state outlives the component, same as the queue below - a stale window from the last
        // mount would otherwise defer this one's first refresh for no reason.
        cancelScheduledRefresh();
        lastRefreshAt = 0;

        // Which schools' changes are worth reacting to. Fetched once per mount, never per broadcast.
        // Failure leaves the set empty, which processes everything - the behaviour before this filter
        // existed.
        try {
            refreshScope = new Set((await api.GetRefreshScope() ?? [])
                .filter(gu => !!gu)
                .map(gu => gu.toUpperCase()));
        } catch {
            refreshScope = new Set();
        }

        // ONE LINE, ONCE PER MOUNT, KEPT ON PURPOSE. The scope filter took live refresh down on
        // 2026-08-10 and the failure was SILENT - a discarded broadcast is indistinguishable from a
        // quiet building, so "nothing is happening" was the only symptom and it looked like a dead
        // subscription rather than a working filter with bad inputs. This prints what this grid will
        // accept, so the first question ("is the scope sane?") is answerable from the console instead
        // of by another deploy cycle.
        //
        // The matching per-message log was REMOVED rather than kept: dropping out-of-school
        // broadcasts is the filter's NORMAL high-frequency path, so logging each one would spam the
        // console in exactly the multi-school district this feature exists for.
        console.log('[VM refresh] scope for this grid:',
            refreshScope.size ? Array.from(refreshScope) : '(empty - accepting ALL broadcasts)');

        // NOT FETCHED HERE. window.ST.VmVisitorViewAccess is now published by the page itself
        // (ST_Content.aspx.cs, right after ST.VmReviewToast/ST.VmReviewSound) so the flag exists on
        // EVERY ST page, not just this one - see CRITICAL 1. This component used to fetch it via
        // api.GetVisitorViewAccess() on mount, which meant it was undefined everywhere except the
        // Visitor Check In / Check Out screen itself and also raced messages arriving before the
        // fetch resolved. The page is now the single source of truth; a second writer here would be
        // a bug, not a safety net.
        //
        // ONE LINE, KEPT ON PURPOSE, reporting what the page already published rather than fetching
        // anything: this gate fails closed, so a silent machine needs to be explicable from the
        // console rather than by a deploy. Guarded with ?. because ST itself could in principle be
        // absent this early - dereferencing it unguarded turned a missing global into a thrown error
        // that used to abort this onMount before resetQueueState()/refreshAndOffer() ran below.
        console.log('[VM review] visitor-view access the page published for this user:',
            (window as any).ST?.VmVisitorViewAccess);

        // Start this mount from a known state. The queue is module-scoped and so outlives the
        // component; a wizard still on screen when the grid unmounted would otherwise leave the
        // one-at-a-time slot permanently taken, and auto-open would be silently dead for the rest of
        // the page session with the manual [Review] button still working and nothing to explain why.
        resetQueueState();

        // NO BOOTSTRAP MODAL HANDLERS HERE, DELIBERATELY. Two earlier versions latched the
        // one-at-a-time rule on shown.bs.modal / hidden.bs.modal and both stacked a second wizard on
        // top of the first. The first never received the events (bound to this frame's document.body
        // while ST creates the modal in the ROOT window's document); correcting the binding did not
        // fix the stacking either, so the events do not arrive in the shape the rule needs. The queue
        // now decides purely from whether the modal is actually in the DOM - see
        // VisitorManagementListenerContext. Nothing to bind, and so nothing to unbind.

        // Covers the navigate-to-the-screen case: rows already flagged before this grid was opened.
        // Same code path as the broadcast-driven case rather than a second mechanism.
        await refreshAndOffer();
    })

    onCleanup(async () => {
        // Kill the DOM watcher so no interval outlives the screen.
        stopQueue();
        cancelScheduledRefresh();

        await api.StopListening();
        console.log('stopping listener for visitor management events...');
    })

    // data-st-visitor-management-listener is how listenerOnScreen() finds this element to test
    // whether the Visitor screen is actually in front of the user. A DATA ATTRIBUTE, not the CSS
    // module class: module class names are hashed at build time, so a selector built from one would
    // silently stop matching after a rebuild - and that failure looks like a wizard that never opens,
    // which is indistinguishable from a quiet lobby.
    return (
        <div data-st-visitor-management-listener class={styles['st-visitor-management-listener']}>
            {props.children}
        </div>
    );
}

VisitorManagementListener.defaultProps = new VisitorManagementListenerProps();

registerServerMessage('VisitorManagementRefreshMessage', (data: { ChangedByUserGU: string, OrganizationYearGUList?: string }) => {
    // NOT MY SCHOOL, NOT MY PROBLEM. The SSE channel carries no organization - subscribers are keyed by
    // (userGU, sessionID) alone - so this handler runs on EVERY subscribed grid in the district for
    // EVERY check-in anywhere in it. Before this check each of those clients answered a change it
    // could not see with a full grid re-read and a review-queue fetch: two server round trips, per
    // client, per write, to render nothing different.
    //
    // BOTH SIDES FAIL OPEN. An absent stamp means the writer's focus could not be resolved and an
    // empty scope means we could not determine ours; either way the message is processed. The filter
    // only ever discards a change it can positively place in schools this grid does not cover.
    //
    // A LIST ON BOTH SIDES, PARSED THE SAME WAY ON BOTH SIDES. This is the bug that took live refresh
    // down on 2026-08-10 and it is worth stating exactly. The stamp comes from
    // Context.FocusOrgYearGUAtLeaf, whose summary says "the ONE org year gu" - it is not one. It
    // returns OrgAccessNode.OrgYearGUList, which User.cs:4249 builds as a COMMA-SPACE-DELIMITED LIST,
    // and IsFocusAtLeafOrganization gates it only on orgs.Count == 1, which the framework's own
    // comment (User.cs:4366) notes is true even at a PARENT org when the user has one org. So the
    // writer can legitimately stamp "GU1, GU2". Comparing that whole string against a Set of
    // individual GUs never matches, every broadcast was discarded, and ALL automatic behaviour
    // stopped - silently, because a dropped refresh looks exactly like a quiet building.
    //
    // Parse both sides identically and intersect. Match on ANY overlap: the writer's focus and this
    // grid's focus only have to share one org year for the change to be one this user can see.
    const changedOrgYearGUs = (data?.OrganizationYearGUList ?? '')
        .split(',')
        .map(gu => gu.trim().toUpperCase())
        .filter(gu => !!gu);
    if (changedOrgYearGUs.length > 0
        && refreshScope.size > 0
        && !changedOrgYearGUs.some(gu => refreshScope.has(gu))) {
        return;
    }

    // Coalesced rather than run inline - one visitor produces several writes and so several
    // broadcasts. See scheduleRefresh.
    scheduleRefresh()
});
